import type { ReactNode } from "react";

export interface TechnicalDetailsProps {
  /** What the reader is choosing to open. Defaults to "Technical details". */
  readonly summary?: string;
  readonly children: ReactNode;
  readonly className?: string;
}

/**
 * A collapsed disclosure for figures that describe **how CloudoX works** rather
 * than what it found.
 *
 * The distinction matters for trust. A run page that leads with token counts and
 * model names reads like an internal dashboard and invites the reader to judge
 * the report by its machinery; the same figures behind one click are available
 * to whoever wants them without competing with the environment's findings.
 *
 * Native `<details>` on purpose: it is open-able before hydration, keyboard
 * operable, exposed to assistive tech as a disclosure, and honours the
 * browser's find-in-page — none of which a hand-rolled toggle gets for free.
 */
export function TechnicalDetails({
  summary = "Technical details",
  children,
  className,
}: TechnicalDetailsProps) {
  return (
    <details className={className}>
      <summary
        className="cursor-pointer list-none text-xs font-medium underline decoration-dotted underline-offset-4"
        style={{ color: "var(--fg-subtle)" }}
      >
        {summary}
      </summary>
      <div className="mt-3">{children}</div>
    </details>
  );
}
